<script setup lang="ts">
import { computed } from 'vue';
import { useRoute } from 'vue-router';
import GaiaBrandPanel from '@/components/design/domain/auth/GaiaBrandPanel.vue';
import { gaiaNeutral } from '@/components/design/themes/tokens';
import authLoop from '@/assets/video/auth-loop.mp4';
import logoUrl from '@/assets/logo.svg';

/**
 * The shell every web auth page sits in. It lives here rather than in each page
 * because all seven of them (login, sign-up, sign-up/complete, verify-email,
 * invite/accept, the CM funnel and the OAuth callback) draw the same chrome —
 * the page supplies only its card.
 *
 * Two forms, and which one a route gets is the design's rule, not a
 * convenience: the brand panel is for the two *entry* screens (login,
 * sign-up), where someone is deciding whether to come in. Every later step is
 * somewhere they were already sent, so the panel would be selling to someone
 * who has already bought — those get the mark above the card instead.
 */
const route = useRoute();

const showBrand = computed(() => route.meta.authBrand === true);
const showLogo = computed(() => !showBrand.value);

const canvas = gaiaNeutral.surfaceCanvas;
</script>

<template>
  <!-- Card left, brand panel right. The card is also FIRST in the DOM, which is
       the reading order we want anyway — so this is a plain `row`. (The mock
       gets the same picture the other way round: panel first + row-reverse.) -->
  <div class="gaia-auth-layout" :style="{ background: canvas }">
    <div class="column">
      <div class="card-slot">
        <img v-if="showLogo" :src="logoUrl" alt="GAIA" class="logo" />
        <router-view />
      </div>
    </div>

    <gaia-brand-panel
      v-if="showBrand"
      class="panel"
      headline="Manage your contracts seamlessly"
      subline="Create, review, sign, store and analyze all your contracts in one seamless AI native system."
    >
      <template #media>
        <!-- Decorative: muted + autoplay so it can start without a gesture,
             playsinline so iOS doesn't take it fullscreen, and aria-hidden
             because it carries nothing a screen reader needs. -->
        <video :src="authLoop" autoplay muted loop playsinline aria-hidden="true" />
      </template>
      <template #logo>
        <img :src="logoUrl" alt="GAIA" />
      </template>
      <template #footer>GAIA © {{ new Date().getFullYear() }}</template>
    </gaia-brand-panel>
  </div>
</template>

<style scoped>
.gaia-auth-layout {
  min-height: 100vh;
  display: flex;
}

.panel {
  flex: 1 1 44%;
  min-width: 0;
}

.column {
  flex: 1 1 56%;
  min-width: 0;
  /* No panel means the column is the whole page, so the card centres against
     the viewport rather than against a 56% slice of it. */
  display: flex;
  align-items: center;
  justify-content: center;
  /* Bottom-heavy: the card sits slightly above the optical centre, which is
     where the eye expects it on a tall screen. */
  padding: 48px 24px 110px;
}

/* Caps the column; each page's GaiaAuthCard sets its own width within it. */
.card-slot {
  width: 100%;
  max-width: 560px;
}

.logo {
  height: 22px;
  width: auto;
  display: block;
  margin: 0 auto 28px;
}

/* The panel is the first thing to go: below this the card needs the whole
   viewport, and a 44% column of video is decoration the page can spare. */
@media (max-width: 900px) {
  .panel {
    display: none;
  }

  .column {
    padding: 40px 20px 64px;
  }
}
</style>
